import { useState, useEffect, useMemo } from "react";
import { Redirect } from "react-router-dom";
import { Grid, Typography, Divider } from "@material-ui/core";
import { useParams } from "react-router-dom";
import ReadleeAudioPlayer from "../Screens/BookScreen/QueueCards/ReadleeAudioPlayer";
import ItemNav from "../AssignmentReport/ItemNav";
import QuestionCard from "../AssignmentReport/QuestionCard";
import EmptyState from "../EmptyState";
import Loading from "../Loading/Loading";
import useParamsSelector, { getAssignmentClass, getAssignment } from "../../selectors";
import FirebaseService from "../../services/firebase/FirebaseService";
import UploadService from "../../services/upload/UploadService";
import ComprehensionQuestion from "../../interfaces/ComprehensionQuestion";
import ComprehensionMessage from "../../interfaces/ComprehensionMessage";
import ClassroomAssignmentDetails from "../../interfaces/ClassroomAssignmentDetails";

interface QuestionProps {
  question: ComprehensionQuestion;
  url: string | null;
}
const Question = ({ question, url }: QuestionProps) => {
  const [duration, setDuration] = useState<number>(0);

  if (!(question.commentType === "text" || url)) return <Loading paddingTop="0" />;
  return (
    <Grid container alignContent="center">
      <Typography variant="h3" className="label">
        Q:
      </Typography>
      {question.commentType === "text" ? (
        <Typography className="question">{question.textComment}</Typography>
      ) : (
        url && <ReadleeAudioPlayer audioUrls={[url]} audioDurations={[duration]} onDuration={setDuration} narrow />
      )}
    </Grid>
  );
};

type DurationMap = { [key: string]: number };
interface ResponseCommentsProps {
  urls: Array<string>;
  responses: ComprehensionMessage["responses"];
}
const ResponseComments = ({ urls, responses }: ResponseCommentsProps) => {
  const [durations, setDurations] = useState<DurationMap>({});
  return (
    <div>
      {responses.map(({ comment, timeStamp }, idx) => (
        <div key={timeStamp}>
          {comment.commentType === "text" ? (
            <div className="textComment">{comment.textComment}</div>
          ) : urls[idx] ? (
            <ReadleeAudioPlayer
              audioUrls={[urls[idx] || ""]}
              audioDurations={[durations[idx] || 0]}
              onDuration={(dur: number) => setDurations(state => ({ ...state, [idx]: dur }))}
              narrow
            />
          ) : (
            <Loading paddingTop="10px" />
          )}
        </div>
      ))}
    </div>
  );
};

const getStudentId = (assignmentClass: { [key: string]: ClassroomAssignmentDetails } = {}) => {
  const studentIds = Object.values(assignmentClass).flatMap(ac => Object.keys(ac.students || {}));
  return studentIds[0];
};

const ComprehensionQuestionCard = () => {
  const { assignmentId, studentId } = useParams<{ assignmentId: string; studentId?: string }>();
  const assignmentClass = useParamsSelector(getAssignmentClass);
  const assignment = useParamsSelector(getAssignment);
  const comprehensionQuestions = useMemo(
    () => assignment?.assignmentDetails.assignmentComments?.comprehensionQuestions || [],
    [assignment],
  );

  const [index, setIndex] = useState(0);
  const [userComment, setUserComment] = useState<ComprehensionMessage | null>(null);
  const [questionAudioUrl, setQuestionAudioUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<Array<string>>([]);

  const currentQuestion = comprehensionQuestions[index];
  const currentResponses = userComment?.responses;
  const redirectStudentId = getStudentId(assignmentClass);

  useEffect(() => {
    const questionId = comprehensionQuestions[index]?.id;
    if (!(studentId && questionId)) return;
    const fetchUserComment = async () => {
      setUserComment(null);
      const data = await FirebaseService.getUserComment(studentId, questionId);
      setUserComment(data);

      const responses = data?.responses.map(({ comment }) =>
        comment?.audioPath ? UploadService.getCommentAudio(comment.audioPath) : Promise.resolve(""),
      );

      setAudioUrl(await Promise.all(responses || []));
    };
    fetchUserComment();
  }, [index, comprehensionQuestions, studentId]);

  useEffect(() => {
    const fetchQuestionUrl = async () => {
      setQuestionAudioUrl(null);
      if (currentQuestion?.audioPath) {
        setQuestionAudioUrl(
          currentQuestion?.audioPath ? await UploadService.getCommentAudio(currentQuestion.audioPath) : null,
        );
      }
    };
    fetchQuestionUrl();
  }, [currentQuestion]);

  if (!studentId) {
    return <Redirect to={`/Assignments/${assignmentId}/students/${redirectStudentId}`} />;
  }
  if (!currentQuestion) return null;

  return (
    <div className="comprehensionQuestionCard">
      <Grid className="titleContainer" container wrap="nowrap" justifyContent="space-between">
        <Typography variant="subtitle1">Comprehension Question #{index + 1}</Typography>
        <ItemNav index={index} setIndex={setIndex} length={comprehensionQuestions.length} />
      </Grid>
      <QuestionCard>
        <Question question={currentQuestion} url={questionAudioUrl} />
        <Divider />
        {currentResponses ? (
          <Grid container>
            <Typography variant="h3" className="label">
              A:
            </Typography>
            <ResponseComments urls={audioUrl} responses={currentResponses} />
          </Grid>
        ) : (
          <EmptyState>No response for this question</EmptyState>
        )}
      </QuestionCard>
    </div>
  );
};

export default ComprehensionQuestionCard;
